import React, { useMemo } from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { SkeletonText } from '../../shared/Skeleton';
import { EmptyState, Pager, usePaged, fmtNumber } from '../kit';
import { SURFACE } from '../layout';
import CardHeader from './CardHeader';
import { BlockError } from './states';
import RailRow from './RailRow';
import { CARD_PAGE_SIZE } from './cardPageSize';
import CountBadge from './CountBadge';
import { requestTone } from '../metaTone';

// What the client asked us for (spec §4). Data: v_client_requests — client_id ·
// client · requested_by · task_id · title · status · requested · delivered ·
// days_to_deliver · still_open · days_waiting. Open requests sort first, longest
// wait first: a request open 300 days is a different failure from an internal task.

const isOpen = (r) => r?.still_open === true;
/** Whether a request is still open. */
export const isOpenRequest = isOpen;

/** "Still open — 41 days waiting" / "Delivered in 3 days". */
export const statusLine = (r) => {
  if (isOpen(r)) {
    const d = r?.days_waiting;
    return d == null ? 'Still open' : `Still open — ${fmtNumber(d)} days waiting`;
  }
  const d = r?.days_to_deliver;
  return d == null ? 'Delivered' : `Delivered in ${fmtNumber(d)} days`;
};

/**
 * A request's detail line as coloured parts: who asked, then the status ("Still open — 41 days waiting" amber,
 * red from 30 days; "Delivered in 3 days" green).
 * @param {object} r  A v_client_requests row.
 * @returns {Array<{text: string, tone: string|null}>}
 */
export const requestMetaParts = (r) => [
  r?.requested_by ? { text: r.requested_by, tone: null } : null,
  { text: statusLine(r), tone: requestTone(r) },
].filter(Boolean);

/**
 * Open requests first, longest wait first, then delivered.
 * @param {object[]} [list]  v_client_requests rows
 * @returns {object[]}
 */
export const sortRequests = (list) =>
  [...(list || [])].sort((a, b) => {
    const ao = isOpen(a) ? 1 : 0;
    const bo = isOpen(b) ? 1 : 0;
    if (ao !== bo) return bo - ao;
    return (Number(b?.days_waiting) || 0) - (Number(a?.days_waiting) || 0);
  });

/**
 * Presentational: the page owns the v_client_requests read so a page-level search
 * can filter the very rows shown here.
 *
 * @param {object} props
 * @param {string} props.clientId       Resets the pager when the client changes.
 * @param {object[]} [props.requests]   Loaded rows (already search-filtered by the page).
 * @param {boolean} [props.loading]
 * @param {*} [props.error]
 * @param {() => void} [props.onRetry]
 * @param {React.ReactNode} [props.action]  Header action (e.g. the drill link).
 * @param {string} [props.emptyMessage]     Overrides the empty copy (a search with no match).
 */
export default function RequestsCard({
  clientId, requests, loading, error, onRetry, action = null,
  emptyMessage = 'No recorded requests from this client.',
}) {
  const rows = useMemo(() => sortRequests(requests), [requests]);
  const { pageRows, page, setPage, pageCount, pageSize, total } = usePaged(rows, clientId, CARD_PAGE_SIZE);

  return (
    <PanelErrorBoundary label="Requests">
      <section className={SURFACE} aria-label="Requests">
        <CardHeader
          title="What they asked us for" action={action}
          badge={!loading && !error && rows.length > 0 ? <CountBadge count={rows.length} noun="request" testId="requests-count" /> : null}
        />
        {loading ? (
          <div className="mt-3" role="status" aria-label="Loading">
            <SkeletonText lines={3} lineClass="h-10" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : error ? (
          <BlockError error={error} what="this client's requests" message="Couldn't load this client's requests." onRetry={onRetry} />
        ) : rows.length === 0 ? (
          <EmptyState variant="inset" message={emptyMessage} />
        ) : (
          <div className="mt-4 space-y-2.5">
            {pageRows.map((r) => (
              <RailRow
                key={r.task_id || `${r.requested_by}-${r.title}`} testId="request-row" rail={isOpen(r) ? 'warn' : 'neutral'}
                title={r?.title || 'An unnamed request'} meta={requestMetaParts(r)}
              />
            ))}
            <Pager page={page} pageCount={pageCount} pageSize={pageSize} total={total} onPage={setPage} />
          </div>
        )}
      </section>
    </PanelErrorBoundary>
  );
}
